import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Search, PencilLine, Sparkles, Camera, Crown, Loader2, Plus, Check, Lock, Star, ArrowLeft, Clock, Barcode,
} from 'lucide-react';
import { UNITS, MEALS, suggestedMeal, unitToGrams, scaleFood } from './trackingConstants';
import { BarcodeScannerModal } from '../../components/BarcodeScannerModal';

const TABS = [
  { key: 'search', fr: 'Recherche', en: 'Search', icon: Search },
  { key: 'photo', fr: 'Caméra', en: 'Camera', icon: Camera, premium: true },
  { key: 'barcode', fr: 'Code-barres', en: 'Barcode', icon: Barcode },
  { key: 'ai', fr: 'Voix/texte', en: 'Voice/text', icon: Sparkles },
  { key: 'manual', fr: 'Manuel', en: 'Manual', icon: PencilLine },
];

const UnitSelect = ({ value, onChange, en }) => (
  <select
    value={value} onChange={(e) => onChange(e.target.value)}
    className="bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none"
    data-testid="unit-select"
  >
    {UNITS.map((u) => <option key={u.key} value={u.key}>{en ? u.en : u.fr}</option>)}
  </select>
);

const MacroPreview = ({ m }) => (
  <div className="flex items-center gap-3 text-xs text-zinc-400 mt-1" data-testid="macro-preview">
    <span className="font-bold text-white">{Math.round(m.calories)} kcal</span>
    <span>P {Math.round(m.protein)}g</span>
    <span>G {Math.round(m.carbs)}g</span>
    <span>L {Math.round(m.fat)}g</span>
  </div>
);

/* ---------- Onglet recherche base d'aliments (locale + Open Food Facts) ---------- */
const SearchTab = ({ api, onAdd, busy, en }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(100);
  const [unit, setUnit] = useState('g');
  const [searching, setSearching] = useState(false);
  const tRef = useRef();
  const dn = (f) => (en ? (f.name_en || f.name) : f.name);

  useEffect(() => {
    if (selected || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      api(`/api/tracking/foods/search?q=${encodeURIComponent(q.trim())}&lang=${en ? 'en' : 'fr'}`)
        .then((d) => setResults(d.foods || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 280);
    return () => clearTimeout(tRef.current);
  }, [q, selected, api, en]);

  const grams = selected ? unitToGrams(selected, Number(qty) || 0, unit) : 0;
  const macros = selected ? scaleFood(selected, grams) : null;

  const confirmAdd = () => {
    const quantity = Number(qty) || 1;
    if (selected.food_id) {
      onAdd({ food_id: selected.food_id, quantity, unit });
    } else {
      const k = grams / 100;
      onAdd({
        food_name: dn(selected), quantity, unit,
        calories: macros.calories, protein: macros.protein, carbs: macros.carbs, fat: macros.fat,
        fiber: +((selected.fiber || 0) * k).toFixed(1),
        health_score: selected.health_score ?? null, source: 'off',
      });
    }
  };

  if (selected) {
    return (
      <div data-testid="search-selected">
        <button onClick={() => setSelected(null)} className="text-xs text-zinc-400 hover:text-primary mb-3">← {en ? 'Back to search' : 'Retour à la recherche'}</button>
        <div className="rounded-xl bg-zinc-800/60 border border-white/10 p-4 mb-4">
          <p className="font-bold text-white">{dn(selected)}</p>
          <p className="text-xs text-zinc-500">{selected.kcal} kcal / 100g · P {selected.p} · G {selected.c} · L {selected.f}{selected.source === 'off' ? ' · Open Food Facts' : ''}</p>
        </div>
        <div className="flex gap-2 mb-3">
          <input
            type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
            className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none"
            data-testid="quantity-input"
          />
          <UnitSelect value={unit} onChange={setUnit} en={en} />
        </div>
        {macros && <MacroPreview m={macros} />}
        <button
          onClick={confirmAdd}
          disabled={busy}
          className="mt-4 w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="add-food-confirm"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {en ? 'Add' : 'Ajouter'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={en ? 'Search a food (2+ letters)…' : 'Rechercher un aliment (2+ lettres)…'}
          className="w-full bg-zinc-800 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none"
          data-testid="food-search-input"
        />
      </div>
      <div className="mt-3 max-h-72 overflow-y-auto space-y-1.5" data-testid="food-search-results">
        {searching && <div className="flex items-center gap-2 text-zinc-500 text-sm py-2"><Loader2 className="w-4 h-4 animate-spin" /> {en ? 'Searching…' : 'Recherche…'}</div>}
        {!searching && q.trim().length >= 2 && results.length === 0 && (
          <p className="text-sm text-zinc-500 py-2">{en ? 'No result — try the manual or AI tab.' : 'Aucun résultat — essayez l\'onglet Manuel ou AI.'}</p>
        )}
        {results.map((f, i) => (
          <button
            key={f.food_id || f.off_code || i} onClick={() => { setSelected(f); setUnit('g'); setQty(100); }}
            className="w-full text-left rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-white/5 hover:border-primary/40 px-3 py-2.5 transition-all"
            data-testid="food-result-item"
          >
            <p className="text-sm font-semibold text-white flex items-center gap-1.5">
              {dn(f)}
              {f.source === 'off' && <span className="text-[9px] uppercase tracking-wide text-zinc-500 bg-white/5 px-1.5 py-0.5 rounded">OFF</span>}
            </p>
            <p className="text-xs text-zinc-500">{f.kcal} kcal · P {f.p}g · G {f.c}g · L {f.f}g <span className="text-[10px]">/100g</span></p>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ---------- Onglet code-barres (scan camera + Open Food Facts) ---------- */
const BarcodeTab = ({ api, onAdd, busy, en }) => {
  const [selected, setSelected] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(true);
  const [qty, setQty] = useState(100);
  const [unit, setUnit] = useState('g');

  const handleFound = (food) => { setSelected(food); setUnit('g'); setQty(100); };

  const grams = selected ? unitToGrams(selected, Number(qty) || 0, unit) : 0;
  const macros = selected ? scaleFood(selected, grams) : null;

  const confirmAdd = () => {
    const quantity = Number(qty) || 1;
    if (selected.food_id) {
      onAdd({ food_id: selected.food_id, quantity, unit });
    } else {
      const k = grams / 100;
      onAdd({
        food_name: selected.name, quantity, unit,
        calories: macros.calories, protein: macros.protein, carbs: macros.carbs, fat: macros.fat,
        fiber: +((selected.fiber || 0) * k).toFixed(1),
        health_score: selected.health_score ?? null, source: 'off',
      });
    }
  };

  if (selected) {
    return (
      <div data-testid="barcode-selected">
        <button onClick={() => { setSelected(null); setScannerOpen(true); }} className="text-xs text-zinc-400 hover:text-primary mb-3">← {en ? 'Scan another' : 'Scanner un autre'}</button>
        <div className="rounded-xl bg-zinc-800/60 border border-white/10 p-4 mb-4">
          <p className="font-bold text-white">{selected.name}</p>
          <p className="text-xs text-zinc-500">{selected.kcal} kcal / 100g · P {selected.p} · G {selected.c} · L {selected.f} · Open Food Facts</p>
        </div>
        <div className="flex gap-2 mb-3">
          <input
            type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)}
            className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none"
            data-testid="barcode-quantity-input"
          />
          <UnitSelect value={unit} onChange={setUnit} en={en} />
        </div>
        {macros && <MacroPreview m={macros} />}
        <button
          onClick={confirmAdd}
          disabled={busy}
          className="mt-4 w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="barcode-add-confirm"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {en ? 'Add' : 'Ajouter'}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="barcode-tab">
      <BarcodeScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onFoodFound={handleFound} authFetch={api} en={en} />
      {!scannerOpen && (
        <div className="text-center py-6">
          <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-3">
            <Barcode className="w-6 h-6 text-primary" />
          </div>
          <p className="font-bold text-white">{en ? 'Scan a barcode' : 'Scanner un code-barre'}</p>
          <p className="text-sm text-zinc-400 mt-1 max-w-xs mx-auto">{en ? 'Point your camera at the product barcode to add it instantly.' : "Visez le code-barre du produit avec votre caméra pour l'ajouter instantanément."}</p>
          <button onClick={() => setScannerOpen(true)} className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500" data-testid="barcode-open-scanner-btn">
            <Barcode className="w-4 h-4" /> {en ? 'Open scanner' : 'Ouvrir le scanner'}
          </button>
        </div>
      )}
    </div>
  );
};

/* ---------- Onglet saisie manuelle ---------- */
const ManualTab = ({ onAdd, busy, en }) => {
  const [form, setForm] = useState({ food_name: '', quantity: 1, unit: 'portion', calories: '', protein: '', carbs: '', fat: '' });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const valid = form.food_name.trim() && form.calories !== '';
  return (
    <div className="space-y-3" data-testid="manual-tab">
      <input value={form.food_name} onChange={(e) => set('food_name', e.target.value)} placeholder={en ? 'Food name' : "Nom de l'aliment"} className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-name" />
      <div className="flex gap-2">
        <input type="number" min="1" value={form.quantity} onChange={(e) => set('quantity', e.target.value)} placeholder={en ? 'Qty' : 'Qté'} className="flex-1 bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none" data-testid="manual-qty" />
        <UnitSelect value={form.unit} onChange={(v) => set('unit', v)} en={en} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[['calories', 'Calories (kcal)'], ['protein', en ? 'Protein (g)' : 'Protéines (g)'], ['carbs', en ? 'Carbs (g)' : 'Glucides (g)'], ['fat', en ? 'Fat (g)' : 'Lipides (g)']].map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs text-zinc-500 mb-1">{label}</label>
            <input type="number" min="0" value={form[k]} onChange={(e) => set(k, e.target.value)} className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none" data-testid={`manual-${k}`} />
          </div>
        ))}
      </div>
      <button
        onClick={() => onAdd({ ...form, quantity: Number(form.quantity) || 1, calories: Number(form.calories), protein: Number(form.protein) || 0, carbs: Number(form.carbs) || 0, fat: Number(form.fat) || 0, source: 'manual' })}
        disabled={busy || !valid}
        className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="manual-add-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {en ? 'Add' : 'Ajouter'}
      </button>
    </div>
  );
};

/* ---------- Onglet recherche AI ---------- */
const AITab = ({ api, onAdd, busy, en, onUpgrade }) => {
  const [desc, setDesc] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [limit, setLimit] = useState(false);
  const [remaining, setRemaining] = useState(null);

  const run = () => {
    setErr(''); setLimit(false); setEstimate(null); setLoading(true);
    api('/api/tracking/ai-estimate', { method: 'POST', body: JSON.stringify({ description: desc.trim() }) })
      .then((d) => { setEstimate(d.estimate); setRemaining(d.remaining_today); })
      .catch(async (r) => {
        if (r.status === 402) setLimit(true);
        else setErr(en ? 'Estimation failed, add more details.' : 'Estimation impossible, ajoutez des détails.');
      })
      .finally(() => setLoading(false));
  };

  return (
    <div data-testid="ai-tab">
      <textarea
        value={desc} onChange={(e) => setDesc(e.target.value)} rows={3}
        placeholder={en ? 'e.g. 100g grilled chicken + 150g rice + olive oil…' : 'ex : 100g poulet grillé + 150g riz + un filet d\'huile d\'olive…'}
        className="w-full bg-zinc-800 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm focus:border-primary focus:outline-none resize-none"
        data-testid="ai-description"
      />
      <p className="text-xs text-zinc-500 mt-1">{en ? 'Tip: the more details, the more accurate.' : 'Astuce : plus c\'est détaillé, plus c\'est précis.'}</p>
      <button
        onClick={run} disabled={loading || desc.trim().length < 2}
        className="mt-3 w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="ai-estimate-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {en ? 'Estimate' : 'Estimer'}
      </button>

      {err && <p className="text-red-400 text-sm mt-3" data-testid="ai-error">{err}</p>}

      {limit && (
        <div className="mt-4 rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-4 text-center" data-testid="ai-limit">
          <Crown className="w-7 h-7 text-yellow-400 mx-auto mb-2" />
          <p className="text-sm text-white font-semibold">{en ? 'Free AI limit reached today.' : 'Limite AI gratuite atteinte aujourd\'hui.'}</p>
          <p className="text-xs text-zinc-400 mt-1">{en ? 'Go Suivi Pro for unlimited AI + photo recognition.' : 'Passez à Suivi Pro pour l\'AI illimitée + la reconnaissance photo.'}</p>
          <button onClick={onUpgrade} className="mt-3 px-5 py-2.5 rounded-xl font-bold text-black bg-gradient-to-r from-yellow-400 to-primary text-sm" data-testid="ai-limit-upgrade">{en ? 'Discover Suivi Pro' : 'Découvrir Suivi Pro'}</button>
        </div>
      )}

      {estimate && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-xl bg-zinc-800/60 border border-white/10 p-4" data-testid="ai-estimate-result">
          <p className="font-bold text-white">{estimate.food_name}</p>
          <p className="text-xs text-zinc-500">≈ {estimate.grams} g</p>
          <MacroPreview m={estimate} />
          {remaining != null && <p className="text-[11px] text-zinc-500 mt-2">{en ? `${remaining} free AI searches left today` : `${remaining} recherches AI gratuites restantes aujourd'hui`}</p>}
          <button
            onClick={() => onAdd({ food_name: estimate.food_name, quantity: 1, unit: 'portion', calories: estimate.calories, protein: estimate.protein, carbs: estimate.carbs, fat: estimate.fat, fiber: estimate.fiber, health_score: estimate.health_score, source: 'ai' })}
            disabled={busy}
            className="mt-3 w-full py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="ai-add-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {en ? 'Add this' : 'Ajouter'}
          </button>
        </motion.div>
      )}
    </div>
  );
};

/* ---------- Onglet photo (premium) ---------- */
const PhotoTab = ({ premium, en, onUpgrade, ...rest }) => {
  if (!premium) {
    return (
      <div className="text-center py-6" data-testid="photo-locked">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400/20 to-primary/20 border border-yellow-500/30 flex items-center justify-center mx-auto mb-3">
          <Lock className="w-6 h-6 text-yellow-400" />
        </div>
        <p className="font-bold text-white">{en ? 'Photo recognition' : 'Reconnaissance par photo'}</p>
        <p className="text-sm text-zinc-400 mt-1 max-w-xs mx-auto">{en ? 'Take a photo of your meal, our engine estimates calories & macros. Available with Suivi Pro.' : 'Prenez votre plat en photo, notre moteur estime calories & macros. Inclus dans Suivi Pro.'}</p>
        <button onClick={onUpgrade} className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-black bg-gradient-to-r from-yellow-400 to-primary" data-testid="photo-upgrade-btn">
          <Crown className="w-4 h-4" /> {en ? 'Unlock with Suivi Pro' : 'Débloquer avec Suivi Pro'}
        </button>
      </div>
    );
  }
  return <PhotoUpload en={en} {...rest} />;
};

// Composant d'upload réel (premium) : photo -> base64 -> analyse AI
const PhotoUpload = ({ en, api, onAdd, busy }) => {
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [err, setErr] = useState('');

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(''); setEstimate(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const max = 1024;
        let { width, height } = img;
        if (width > max || height > max) {
          const r = Math.min(max / width, max / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const data = canvas.toDataURL('image/jpeg', 0.82);
        setPreview(data); setB64(data);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const analyze = () => {
    if (!b64) return;
    setLoading(true); setErr('');
    api('/api/tracking/ai-photo', { method: 'POST', body: JSON.stringify({ image_base64: b64 }) })
      .then((d) => setEstimate(d.estimate))
      .catch(() => setErr(en ? 'Analysis failed, try a clearer photo.' : 'Analyse impossible, essayez une photo plus nette.'))
      .finally(() => setLoading(false));
  };

  return (
    <div data-testid="photo-upload">
      <label className="block rounded-2xl border-2 border-dashed border-white/15 hover:border-primary/50 transition-colors cursor-pointer p-6 text-center">
        <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-input" />
        {preview ? (
          <img src={preview} alt="" className="max-h-48 mx-auto rounded-xl" />
        ) : (
          <div className="text-zinc-400">
            <Camera className="w-8 h-8 mx-auto mb-2 text-primary" />
            <p className="text-sm font-semibold">{en ? 'Tap to take / choose a photo' : 'Prenez ou choisissez une photo'}</p>
          </div>
        )}
      </label>

      {b64 && !estimate && (
        <button onClick={analyze} disabled={loading} className="mt-3 w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-yellow-400 to-primary hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2" data-testid="photo-analyze-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {en ? 'Analyze the photo' : 'Analyser la photo'}
        </button>
      )}
      {err && <p className="text-red-400 text-sm mt-3" data-testid="photo-error">{err}</p>}

      {estimate && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-xl bg-zinc-800/60 border border-white/10 p-4" data-testid="photo-estimate-result">
          <p className="font-bold text-white">{estimate.food_name}</p>
          <p className="text-xs text-zinc-500">≈ {estimate.grams} g</p>
          <MacroPreview m={estimate} />
          <button
            onClick={() => onAdd({ food_name: estimate.food_name, quantity: 1, unit: 'portion', calories: estimate.calories, protein: estimate.protein, carbs: estimate.carbs, fat: estimate.fat, fiber: estimate.fiber, health_score: estimate.health_score, source: 'photo' })}
            disabled={busy}
            className="mt-3 w-full py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="photo-add-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {en ? 'Add this' : 'Ajouter'}
          </button>
        </motion.div>
      )}
    </div>
  );
};

/* ---------- Étape 1 : choix du moment du repas (cartes animées) ---------- */
const MEAL_HINT = {
  breakfast: { fr: 'Le matin', en: 'Morning' },
  lunch: { fr: 'Le midi', en: 'Midday' },
  snack: { fr: 'En-cas', en: 'Snack' },
  gouter: { fr: "L'après-midi", en: 'Afternoon' },
  dinner: { fr: 'Le soir', en: 'Evening' },
};

const MealPicker = ({ en, onPick }) => {
  const suggested = suggestedMeal();
  return (
  <div data-testid="meal-picker">
    <p className="text-sm text-zinc-400 mb-4">
      {en ? 'Which meal are you logging?' : 'Pour quel repas souhaitez-vous ajouter ?'}
    </p>
    <div className="grid grid-cols-2 gap-3">
      {MEALS.map((m, i) => {
        const isSuggested = m.key === suggested;
        return (
        <motion.button
          key={m.key}
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: i * 0.05, type: 'spring', stiffness: 260, damping: 20 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => onPick(m.key)}
          className={`group relative overflow-hidden rounded-2xl border p-4 text-left transition-colors ${m.key === 'dinner' ? 'col-span-2' : ''} ${isSuggested ? 'border-primary/60 ring-2 ring-primary/40 bg-gradient-to-br from-primary/20 to-zinc-900' : 'border-white/10 bg-gradient-to-br from-zinc-800/80 to-zinc-900 hover:border-primary/50'}`}
          data-testid={`pick-meal-${m.key}`}
        >
          <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full blur-2xl transition-colors ${isSuggested ? 'bg-primary/30' : 'bg-primary/10 group-hover:bg-primary/25'}`} />
          {isSuggested && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/90 text-[10px] font-bold text-white" data-testid="suggested-badge">
              <Clock className="w-2.5 h-2.5" /> {en ? 'Suggested' : 'Suggéré'}
            </span>
          )}
          <div className="relative flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/25 to-yellow-500/20 flex items-center justify-center shrink-0">
              <m.icon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-bold text-white">{en ? m.en : m.fr}</p>
              <p className="text-xs text-zinc-500">{en ? MEAL_HINT[m.key].en : MEAL_HINT[m.key].fr}</p>
            </div>
          </div>
        </motion.button>
        );
      })}
    </div>
  </div>
  );
};

export const AddFoodSheet = ({ open, onClose, meal, date, api, onAdded, en, premium, onUpgrade }) => {
  const [step, setStep] = useState('pick-meal');
  const [chosenMeal, setChosenMeal] = useState(meal || 'breakfast');
  const [tab, setTab] = useState('search');
  const [busy, setBusy] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const mealMeta = MEALS.find((m) => m.key === chosenMeal) || MEALS[0];

  useEffect(() => {
    if (!open) return;
    setTab('search');
    if (meal) { setChosenMeal(meal); setStep('method'); }
    else { setStep('pick-meal'); }
    api('/api/tracking/favorites').then((d) => setFavorites(d.favorites || [])).catch(() => {});
  }, [open, api, meal]);

  const handleAdd = async (payload) => {
    setBusy(true);
    try {
      await api('/api/tracking/log', { method: 'POST', body: JSON.stringify({ ...payload, meal: chosenMeal, date }) });
      onAdded();
      onClose();
    } catch (e) {
      // erreur silencieuse, l'utilisateur peut réessayer
    } finally {
      setBusy(false);
    }
  };

  const handleAddFavorite = (fav) => {
    const payload = fav.food_id
      ? { food_id: fav.food_id, quantity: fav.quantity || 1, unit: fav.unit || 'g' }
      : {
          food_name: fav.name, quantity: fav.quantity || 1, unit: fav.unit || 'portion',
          calories: fav.calories, protein: fav.protein, carbs: fav.carbs, fat: fav.fat,
          fiber: fav.fiber, health_score: fav.health_score, source: fav.source || 'manual',
        };
    handleAdd(payload);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="add-food-sheet"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="relative w-full sm:max-w-lg bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
          >
            {step === 'pick-meal' ? (
              <>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-bold text-white">{en ? 'Add a meal' : 'Ajouter un repas'}</h3>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="add-food-close"><X className="w-5 h-5" /></button>
                </div>
                <MealPicker en={en} onPick={(k) => { setChosenMeal(k); setStep('method'); }} />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {!meal && (
                      <button onClick={() => setStep('pick-meal')} className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="back-to-meals"><ArrowLeft className="w-4 h-4" /></button>
                    )}
                    <mealMeta.icon className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-bold text-white">{en ? mealMeta.en : mealMeta.fr}</h3>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="add-food-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="flex gap-2.5 mb-5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="method-tabs">
                  {TABS.map((t) => (
                    <button
                      key={t.key} onClick={() => setTab(t.key)}
                      className={`shrink-0 flex flex-col items-center gap-1.5 w-[76px] py-3 rounded-2xl border transition-all ${tab === t.key ? 'bg-primary/15 border-primary text-white' : 'bg-zinc-800/60 border-white/5 text-zinc-400 hover:border-white/20 hover:text-white'}`}
                      data-testid={`tab-${t.key}`}
                    >
                      <span className="relative">
                        <t.icon className={`w-5 h-5 ${tab === t.key ? 'text-primary' : ''}`} />
                        {t.premium && <Crown className="w-3 h-3 text-yellow-400 absolute -top-1.5 -right-2" />}
                      </span>
                      <span className="text-[11px] font-semibold leading-tight text-center">{en ? t.en : t.fr}</span>
                    </button>
                  ))}
                </div>

                {favorites.length > 0 && (
                  <div className="mb-4" data-testid="favorites-row">
                    <p className="text-xs font-semibold text-zinc-400 mb-2 flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-yellow-400" /> {en ? 'Favorites' : 'Favoris'}</p>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {favorites.map((fav) => (
                        <button
                          key={fav.fav_id} onClick={() => handleAddFavorite(fav)} disabled={busy}
                          className="shrink-0 px-3 py-1.5 rounded-full bg-zinc-800 border border-white/10 hover:border-yellow-500/50 text-xs text-white transition-colors disabled:opacity-50"
                          data-testid="favorite-chip"
                        >
                          {fav.name} · {Math.round(fav.calories)} kcal
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {tab === 'search' && <SearchTab api={api} onAdd={handleAdd} busy={busy} en={en} />}
                {tab === 'manual' && <ManualTab onAdd={handleAdd} busy={busy} en={en} />}
                {tab === 'ai' && <AITab api={api} onAdd={handleAdd} busy={busy} en={en} onUpgrade={onUpgrade} />}
                {tab === 'photo' && <PhotoTab premium={premium} en={en} onUpgrade={onUpgrade} api={api} onAdd={handleAdd} busy={busy} />}
                {tab === 'barcode' && <BarcodeTab api={api} onAdd={handleAdd} busy={busy} en={en} />}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddFoodSheet;
